// Admin-Konsole — ein Fenster mit drei Tabs statt vier Knoepfen im Profil-Panel.
//
// Das Panel ist 16 rem breit. Jeder neue Admin-Belang machte es bisher laenger, nicht
// uebersichtlicher, und die Knoepfe standen gleichrangig nebeneinander, obwohl sie
// verschiedene Dinge tun: Benutzerverwaltung, Provider-Schluessel, App-Wartung. Die
// Konsole traegt den Rahmen (Backdrop, Kopf, Schliessen, Tabs), die drei Panes liefern
// nur Inhalt.
//
// Die Token-Kosten sassen bis 07.09.2026 mit im APIs-Tab und sind seither wieder ein
// eigener Menuepunkt im Profil-Panel (platform/shared/llm-costs-overlay.js).
//
// Seit 10.09.2026 oeffnet die Konsole fuer JEDE angemeldete Person: der erste Tab
// „Konto" (Stufe, Passwort, Claude-API-Key — platform/shared/konto-pane.js) stand
// vorher als Block im Profil-Panel. Die drei Admin-Tabs erscheinen nur fuer Rolle
// `admin`; ihre Routen pruefen das serverseitig ohnehin.
//
// Spec: docs/superpowers/specs/2026-08-24-admin-konsole-tabs-design.md

import { authApi, ppButton, ppHint, ppLabel, ppLink } from './platform-api.js';
import { renderKontoPane, KONTO_CSS } from './konto-pane.js';
import { renderGeneralPane, ADMIN_CSS } from './admin-overlay.js';
import { renderAppsPane, APPS_CSS } from './admin-apps-pane.js';
import { attachScrollDashes } from './scroll-dashes.js';
import { akkordeonEintrag, AKKORDEON_CSS } from './admin-akkordeon.js';

const CSS = `
.mednos-konsole-backdrop {
  position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5);
  z-index: 10010; display: none;
}
.mednos-konsole-backdrop.is-open { display: block; }
.mednos-konsole {
  position: fixed; left: 50%; top: 50%; transform: translate(-50%, -50%);
  width: min(56rem, 94vw); max-height: 86vh;
  padding: 20px; display: none; flex-direction: column; gap: 16px;
  background: #1a1a1a; color: #fff;
  border: none; border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  z-index: 10011;
  font-family: 'Inter', system-ui, sans-serif;
}
.mednos-konsole.is-open { display: flex; }
.mednos-konsole__head { display: flex; align-items: center; gap: 12px; }
.mednos-konsole__title { font-size: 1rem; font-weight: 600; }
.mednos-konsole__close {
  margin-left: auto; flex-shrink: 0;
  width: 1.6rem; height: 1.6rem; padding: 0;
  border: none; background: transparent; color: #fff;
  font-size: 1.1rem; line-height: 1; cursor: pointer; border-radius: 0.5rem;
  transition: opacity 0.15s;
}
.mednos-konsole__close:hover { opacity: 0.65; }
/* Tabs oben links: drei Eintraege rechtfertigen keine zweite Navigationsebene in
   der Flaeche, und eine Seitenleiste wuerde den ohnehin breiten Inhalt einschnueren. */
.mednos-konsole__tabs {
  display: flex; gap: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.mednos-konsole__tab {
  padding: 8px 14px; border: none; background: transparent; color: #b0b0b0;
  font-family: inherit; font-size: 0.85rem; font-weight: 500; cursor: pointer;
  border-bottom: 2px solid transparent; margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;
}
.mednos-konsole__tab:hover { color: #fff; }
.mednos-konsole__tab.is-aktiv { color: #fff; border-bottom-color: #fff; }
/* Der Wrapper traegt das Ein-/Ausblenden und den Bezugsrahmen fuer die Striche;
   gescrollt wird darin die Pane. Beides in einem Element ginge nicht: ein Element
   mit overflow-y kann keinen absolut positionierten Indikator halten, der beim
   Scrollen stehen bleibt. */
.mednos-konsole__wrap {
  display: none; position: relative; flex: 1; min-height: 0;
}
.mednos-konsole__wrap.is-aktiv { display: flex; }
.mednos-konsole__pane {
  display: flex; flex-direction: column; gap: 16px;
  overflow-y: auto; min-height: 0; flex: 1;
  padding-right: 18px;
}
/* Native Scrollbar weg — ersetzt durch die Striche, wie in Study. */
.mednos-konsole__pane { scrollbar-width: none; }
.mednos-konsole__pane::-webkit-scrollbar { display: none; }

/* Strich-Indikator: eine Spalte kurzer Striche, der an der aktuellen Position
   leuchtet, Klick springt dorthin. Gleiche Gestalt wie in Study, aber auf die
   Farben des Panels gesetzt und auf .mednos-konsole eingegrenzt, damit die Regeln
   nicht mit Studys eigenen kollidieren. */
.mednos-konsole .scroll-dashes {
  position: absolute; top: 0; right: 2px; bottom: 0; width: 14px;
  display: flex; flex-direction: column; align-items: center;
  justify-content: space-between; padding: 4px 0;
  box-sizing: border-box; pointer-events: none;
}
.mednos-konsole .scroll-dash {
  width: 7px; height: 2px; border-radius: 1px;
  background: #b0b0b0; opacity: 0.35;
  pointer-events: auto; cursor: pointer;
  transition: opacity 0.15s ease, width 0.15s ease, background 0.15s ease;
}
.mednos-konsole .scroll-dash:hover { opacity: 0.7; }
.mednos-konsole .scroll-dash.is-active { opacity: 1; width: 12px; background: #fff; }
.mednos-keys__zeile {
  display: flex; align-items: center; gap: 12px;
  padding: 7px 12px; border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
}
.mednos-keys__name {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
}
.mednos-keys__gesetzt, .mednos-keys__fehlt {
  margin-left: auto; flex-shrink: 0; font-size: 0.75rem;
}
.mednos-keys__gesetzt { color: #b0b0b0; }
/* Ein fehlender Schluessel ist kein Fehler, nur eine Tatsache - deshalb gedaempft
   warm statt alarmrot, wie die Fehlerfarbe des Panels (#f0a5a5). */
.mednos-keys__fehlt { color: #f0a5a5; }
.mednos-keys__rotieren { margin-left: 12px; flex-shrink: 0; }
/* Im Eingabezustand behaelt der Name seine Breite, das Feld nimmt den Rest. */
.mednos-keys__name { flex-shrink: 0; }
.mednos-keys__feld { flex: 1; min-width: 6rem; }
`;

/**
 * Die Tabs in Anzeigereihenfolge. `render(pane, konsole)` baut den Inhalt und wird
 * bei JEDEM Wechsel auf diesen Tab erneut aufgerufen — die Listen sollen aktuell
 * sein, wenn man nach einer Aenderung zurueckwechselt, und alle drei Panes bauen
 * ihren Inhalt ohnehin per replaceChildren neu auf.
 */
const ALLE_TABS = [
  { id: 'konto', titel: 'Konto', render: (pane) => renderKontoPane(pane) },
  { id: 'general', titel: 'General', nurAdmin: true, render: (pane) => renderGeneralPane(pane) },
  { id: 'apis', titel: 'APIs', nurAdmin: true, render: (pane) => renderApisPane(pane) },
  { id: 'apps', titel: 'Apps', nurAdmin: true, render: (pane, k) => renderAppsPane(pane, k) },
];

/**
 * Eine Schluesselzeile: Name links, Status rechts, dahinter „Key rotieren".
 *
 * Der Knopf steht auf JEDER Zeile, auch auf einer mit „fehlt" — einen fehlenden
 * Schluessel zu setzen ist derselbe Vorgang wie einen vorhandenen zu ersetzen, und
 * eine Zeile ohne Knopf sah aus, als ginge es dort grundsaetzlich nicht. Welche
 * Schluessel wirklich gesetzt werden duerfen, entscheidet der Server (und dahinter
 * die einkompilierte Tabelle in keyd); die Oberflaeche haelt dafuer keine zweite
 * Liste vor, die auseinanderlaufen koennte.
 */
function schluesselZeile(k, app, neuLaden) {
  const zeile = document.createElement('div');
  zeile.className = 'mednos-keys__zeile';

  const name = document.createElement('span');
  name.className = 'mednos-keys__name';
  name.textContent = k.name;

  const status = document.createElement('span');
  status.className = k.gesetzt ? 'mednos-keys__gesetzt' : 'mednos-keys__fehlt';
  // Die Endung fehlt bei sehr kurzen Werten - dann nur "gesetzt", nicht "gesetzt · ".
  status.textContent = k.gesetzt
    ? (k.endung ? `gesetzt · …${k.endung}` : 'gesetzt')
    : 'fehlt';

  const rotieren = ppLink('Key rotieren', () => oeffneEingabe());
  rotieren.classList.add('mednos-keys__rotieren');

  zeile.append(name, status, rotieren);

  /** Tauscht Status und Knopf gegen ein Eingabefeld — in derselben Zeile. */
  function oeffneEingabe() {
    const feld = document.createElement('input');
    feld.type = 'password';
    feld.className = 'profile-panel__input mednos-keys__feld';
    feld.placeholder = 'neuer Schlüssel';
    feld.autocomplete = 'off';
    feld.spellcheck = false;

    const meldung = document.createElement('span');
    meldung.className = 'mednos-keys__gesetzt';

    const speichern = ppButton('Speichern', async () => {
      const wert = feld.value.trim();
      if (!wert) return;
      speichern.disabled = true;
      // Der Dienst startet nach dem Schreiben neu - ohne Hinweis sieht das wie ein
      // Haenger aus.
      meldung.textContent = 'speichere, Dienst startet neu …';
      // authApi reicht opts unveraendert an fetch weiter - der Body muss also schon
      // serialisiert sein, sonst kaeme "[object Object]" beim Server an.
      const r = await authApi(
        `/api/admin/keys/${encodeURIComponent(app)}/${encodeURIComponent(k.name)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wert }),
        },
      );
      if (r.status === 200) {
        neuLaden();
      } else {
        speichern.disabled = false;
        meldung.textContent = r.body?.fehler || 'Fehlgeschlagen.';
      }
    });

    const abbrechen = ppLink('Abbrechen', () => {
      zeile.replaceChildren(name, status, rotieren);
    });

    zeile.replaceChildren(name, feld, speichern, abbrechen, meldung);
    feld.focus();
  }

  return zeile;
}

/**
 * Ein App-Block als aufklappbare Zeile, zugeklappt startend.
 *
 * Aufgeklappt waeren es vier Ueberschriften und elf Zeilen — im Alltag interessiert
 * meist genau eine davon. Der Zusatz im Kopf sagt deshalb schon zugeklappt, ob
 * etwas fehlt.
 */
function appBlock(eintrag, neuLaden) {
  // Der Schreib-Endpunkt adressiert Apps klein ("papers"), angezeigt wird "Papers".
  const app = eintrag.app.toLowerCase();
  if (eintrag.fehler) {
    return akkordeonEintrag(eintrag.app, 'nicht erreichbar', [
      ppHint(`Nicht erreichbar (${eintrag.fehler}).`),
    ]);
  }
  const schluessel = eintrag.schluessel || [];
  if (!schluessel.length) {
    return akkordeonEintrag(eintrag.app, 'keine', [ppHint('Keine Schlüssel hinterlegt.')]);
  }
  const fehlend = schluessel.filter((k) => !k.gesetzt).length;
  const zusatz = fehlend
    ? `${schluessel.length} Schlüssel · ${fehlend} fehlt`
    : `${schluessel.length} Schlüssel`;
  return akkordeonEintrag(
    eintrag.app,
    zusatz,
    schluessel.map((k) => schluesselZeile(k, app, neuLaden)),
  );
}

function keysFehlerText(status) {
  if (status === 401 || status === 403) return 'Keine Admin-Berechtigung (mehr).';
  return 'Schlüssel-Status derzeit nicht erreichbar.';
}

/**
 * APIs-Tab: Schluessel-Uebersicht aller Apps.
 *
 * Die Token-Kosten standen bis 07.09.2026 als zweiter Block darunter. Sie sind
 * jetzt ein eigener Menuepunkt "Kosten" im Profil-Panel: Schluessel rotieren ist
 * Wartung, Kosten sind ein Blick auf Zahlen — man hat nie beide Anlaesse zugleich,
 * und untereinander gescrollt verdeckte der eine den anderen.
 *
 * Jede App meldet ihre eigenen Variablen - die .env-Dateien gehoeren root und sind
 * fuer die als `mednos` laufenden Dienste nicht lesbar. Der Auth-Server sammelt die
 * Antworten ueber localhost ein und reicht dafuer das Session-Cookie durch; es gibt
 * bewusst kein gemeinsames Dienst-Geheimnis. Klartextwerte kommen hier nie an,
 * nur "gesetzt/fehlt" und die letzten vier Zeichen.
 */
function renderApisPane(container) {
  // Ein Abschnitt INNERHALB der Pane, keine eigene Pane: die Pane-Klasse traegt
  // overflow-y und das display-Umschalten, beides gehoert hier nicht hinein.
  const schluessel = document.createElement('div');
  schluessel.className = 'mednos-admin__section';

  container.replaceChildren(schluessel);
  schluessel.replaceChildren(ppHint('Lade…'));

  authApi('/api/admin/keys').then((r) => {
    if (r.status !== 200 || !r.body?.apps) {
      schluessel.replaceChildren(ppHint(keysFehlerText(r.status)));
      return;
    }
    schluessel.replaceChildren(...r.body.apps.map((a) => appBlock(a, () => renderApisPane(container))));
  }).catch(() => {
    schluessel.replaceChildren(ppHint(keysFehlerText(0)));
  });
}

let stylesEingehaengt = false;
function stelleStylesSicher() {
  if (stylesEingehaengt) return;
  const style = document.createElement('style');
  // Die Panes bringen ihr eigenes CSS mit; die Konsole haengt alles einmalig ein,
  // statt dass jedes Modul beim ersten Aufruf sein eigenes <style> nachschiebt.
  style.textContent = [CSS, ADMIN_CSS, APPS_CSS, AKKORDEON_CSS, KONTO_CSS].join('\n');
  document.head.append(style);
  stylesEingehaengt = true;
}

/**
 * Baut Backdrop + Konsole einmalig und gibt `{ open }` zurueck.
 * @param {{ admin?: boolean }} [opts] Ohne `admin` nur der Konto-Tab.
 */
export function initAdminKonsole({ admin = false } = {}) {
  stelleStylesSicher();
  const TABS = ALLE_TABS.filter((t) => admin || !t.nurAdmin);

  const backdrop = document.createElement('div');
  backdrop.className = 'mednos-konsole-backdrop';
  const box = document.createElement('div');
  box.className = 'mednos-konsole';

  const head = document.createElement('div');
  head.className = 'mednos-konsole__head';
  const title = document.createElement('span');
  title.className = 'mednos-konsole__title';
  title.textContent = admin ? 'Admin Einstellungen' : 'Einstellungen';
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'mednos-konsole__close';
  closeBtn.setAttribute('aria-label', 'Schließen');
  closeBtn.textContent = '✕';
  head.append(title, closeBtn);

  const tabLeiste = document.createElement('div');
  tabLeiste.className = 'mednos-konsole__tabs';
  tabLeiste.setAttribute('role', 'tablist');

  const konsole = { close, open };
  const knoepfe = new Map();
  const panes = new Map();
  const wraps = new Map();

  for (const tab of TABS) {
    const knopf = document.createElement('button');
    knopf.type = 'button';
    knopf.className = 'mednos-konsole__tab';
    knopf.textContent = tab.titel;
    knopf.setAttribute('role', 'tab');
    knopf.addEventListener('click', () => zeigeTab(tab.id));
    tabLeiste.append(knopf);
    knoepfe.set(tab.id, knopf);

    const wrap = document.createElement('div');
    wrap.className = 'mednos-konsole__wrap';
    const pane = document.createElement('div');
    pane.className = 'mednos-konsole__pane';
    pane.setAttribute('role', 'tabpanel');
    const striche = document.createElement('div');
    striche.className = 'scroll-dashes';
    wrap.append(pane, striche);
    panes.set(tab.id, pane);
    wraps.set(tab.id, wrap);

    attachScrollDashes(pane, striche);
  }

  box.append(head, tabLeiste, ...wraps.values());
  document.body.append(backdrop, box);

  function zeigeTab(id) {
    for (const [tabId, knopf] of knoepfe) {
      const aktiv = tabId === id;
      knopf.classList.toggle('is-aktiv', aktiv);
      knopf.setAttribute('aria-selected', String(aktiv));
      wraps.get(tabId).classList.toggle('is-aktiv', aktiv);
    }
    const tab = TABS.find((t) => t.id === id);
    if (!tab) return;
    const pane = panes.get(id);
    tab.render(pane, konsole);
    // Die Striche rechnen bei Scroll und Groessenaenderung nach — nicht aber, wenn
    // sich der INHALT aendert: replaceChildren aendert scrollHeight, nicht
    // clientHeight, da feuert kein ResizeObserver. Deshalb hier EIN gezielter
    // Anstoss nach dem Rendern. Bewusst kein MutationObserver: der beobachtete
    // Knoten und der Indikator haengen im selben Teilbaum, und eine Rueckkopplung
    // zwischen beiden waere ein Aufhaenger, den man erst im Browser merkt.
    requestAnimationFrame(() => pane.dispatchEvent(new Event('scroll')));
  }

  function close() {
    backdrop.classList.remove('is-open');
    box.classList.remove('is-open');
  }

  /**
   * @param {string} [tabId] Auf diesem Tab oeffnen. Ohne Angabe der erste — nach
   *   dem Freischalten will man aber dorthin zurueck, wo man herkam.
   */
  function open(tabId) {
    backdrop.classList.add('is-open');
    box.classList.add('is-open');
    zeigeTab(TABS.some((t) => t.id === tabId) ? tabId : TABS[0].id);
  }

  closeBtn.addEventListener('click', close);
  backdrop.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && box.classList.contains('is-open')) close();
  });

  return { open };
}
